import { useEffect } from 'react';
import { useQuery, useMutation, skipToken } from '@apollo/client/react';

import {
    IContentTypeValue,
    IExtraCategoryChoiceFieldFragment,
    IExtraCategoryOption,
    IExtraCategoryValueFragment,
    ExtraCategoriesDocument,
    UpdateExtraCategoryValuesDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import {
    extraCategoryFormValuesAreUpdated,
    getExtraCategoryInitialFormValues,
    getExtraCategoryValues,
} from 'organisation/utils/extraCategory';
import { TExtraCategoryInput } from 'organisation/types';

interface IProps {
    contentType: IContentTypeValue;
    modelId: string;
    skipQuery?: boolean;
    typeNames: string[];
}

/**
 * Hook to update the extra category values for a model
 * @param contentType The content type of the model
 * @param modelId The ID of the model
 * @param skipQuery Whether to skip the query to get the extra categories
 * @param typeName The names of the types to update in the cache
 */
export const useExtraCategoryValues = ({
    contentType,
    modelId,
    skipQuery,
    typeNames,
}: IProps) => {
    const { showApolloError } = useApolloError();

    const { data, loading, error } = useQuery(
        ExtraCategoriesDocument,
        skipQuery ? skipToken : { variables: { contentType } }
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);
    const [
        updateExtraCategoryValues,
        { loading: updateExtraCategoryValuesLoading },
    ] = useMutation(UpdateExtraCategoryValuesDocument);

    const { extraCategories } = data || {};

    const handleUpdateExtraCategoryValues = async (
        extraCategoryValues: IExtraCategoryValueFragment[],
        newExtraCategoryValues: TExtraCategoryInput,
        // Newly created model id
        targetModelId = modelId
    ) => {
        // Get the initial form values for the extra categories to be able to compare them
        // with the new ones
        const initialExtraCategoryValues = getExtraCategoryInitialFormValues(
            extraCategories,
            extraCategoryValues
        );

        const categoryValuesAreUpdated = extraCategoryFormValuesAreUpdated(
            extraCategories,
            initialExtraCategoryValues,
            newExtraCategoryValues
        );

        if (!categoryValuesAreUpdated) return true;

        const newValues = getExtraCategoryValues(
            newExtraCategoryValues,
            extraCategories
        );

        try {
            await updateExtraCategoryValues({
                variables: {
                    contentType,
                    modelId: targetModelId,
                    values: newValues,
                },
                // Filter options are derived from existing values, so saving
                // values can introduce or remove filter options. Refetch the
                // filters so consumers (e.g. management list filter bars)
                // reflect the change immediately.
                refetchQueries: ['ExtraCategoryFilters'],
                awaitRefetchQueries: true,
                update: (cache, result) => {
                    const { updateExtraCategoryValues } = result.data || {};
                    const { extraCategoryValues: updatedExtraCategoryValues } =
                        updateExtraCategoryValues || {};

                    if (!updatedExtraCategoryValues) return;

                    typeNames.forEach((typeName) => {
                        cache.modify({
                            id: `${typeName}:${targetModelId}`,
                            fields: {
                                extraCategoryValues(
                                    _existingValues = [],
                                    { toReference }
                                ) {
                                    // Replace the old extra category values with the new ones
                                    return (
                                        updatedExtraCategoryValues as IExtraCategoryValueFragment[]
                                    ).map((value) => {
                                        if (
                                            value?.category.categoryType ===
                                            IExtraCategoryOption.Choice
                                        ) {
                                            const { choiceValue } =
                                                value as IExtraCategoryChoiceFieldFragment;

                                            return toReference({
                                                ...value,
                                                choiceValue:
                                                    toReference(choiceValue),
                                            });
                                        }

                                        return toReference(value);
                                    });
                                },
                            },
                        });
                    });
                },
            });
        } catch (error) {
            showApolloError(error);

            return false;
        }

        return true;
    };

    return {
        extraCategories,
        loading: loading || updateExtraCategoryValuesLoading,
        // The initial fetch only, so consumers can gate first render without
        // tearing down on the in-flight update mutation.
        queryLoading: loading,
        updateExtraCategoryValuesLoading,
        handleUpdateExtraCategoryValues,
    };
};
